import React, { useState } from 'react';
import styles from './KellyCriterionCalculator.module.css';
import {handleBlurPercentage, handleFocus } from '../../../utils/calc-utils';

const KellyCriterionCalculator = () => {
  const [calculationMethod, setCalculationMethod] = useState('trueOdds');
  const [fields, setFields] = useState({
    providedOdds: '',
    trueOdds: '',
    trueProbability: '',
    fractionalKelly: '',
  });
  const [result, setResult] = useState('');
  const [isResultVisible, setIsResultVisible] = useState(false);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFields((prevFields) => ({ ...prevFields, [id]: value }));
  };

  const handleCalculate = () => {
    const providedOdds = parseFloat(fields.providedOdds);
    const fractionalKelly = parseFloat(fields.fractionalKelly.replace(/[$,%]/g, '')) / 100;
    let unitSize;

    if (calculationMethod === 'trueOdds') {
      const trueOdds = parseFloat(fields.trueOdds);
      if (!isNaN(providedOdds) && !isNaN(trueOdds) && !isNaN(fractionalKelly)) {
        unitSize = (1 / trueOdds - (1 - 1 / trueOdds) / (providedOdds - 1)) * fractionalKelly * 100;
      }
    } else if (calculationMethod === 'trueProbability') {
      const trueProbability = parseFloat(fields.trueProbability.replace(/[$,%]/g, '')) / 100;
      if (!isNaN(providedOdds) && !isNaN(trueProbability) && !isNaN(fractionalKelly)) {
        unitSize = (trueProbability - (1 - trueProbability) / (providedOdds - 1)) * fractionalKelly * 100;
      }
    }

    if (unitSize !== undefined) {
      if (unitSize > 0) {
        setResult(`The unit size for this selection is: ${unitSize.toFixed(2)}U`);
      } else {
        setResult("You don't have an edge on this bet, therefore bet nothing.");
      }
    } else {
      setResult("Please complete all fields correctly.");
    }
    setIsResultVisible(true);
  };

  return (
    <div className={styles.kellyCriterionCalculator}>
      <h1 className={styles.calculatorTitle}>Kelly Criterion Calculator</h1>
      <div className={styles.containerWrapper}>
        <div className={styles.container}>
          <div className={styles.box}>
            <span className={styles.label}>Calculation Method:</span>
            <select
              className={styles.dropdown}
              id="calcMethod"
              value={calculationMethod}
              onChange={(e) => setCalculationMethod(e.target.value)}
            >
              <option value="trueOdds">Using True Odds</option>
              <option value="trueProbability">Using True Probability</option>
            </select>
          </div>
          <div className={styles.box}>
            <span className={styles.label}>Provided Odds:</span>
            <input
              className={styles.input}
              type="number"
              id="providedOdds"
              value={fields.providedOdds}
              onChange={handleInputChange}
              placeholder="Enter provided odds"
            />
          </div>
          {calculationMethod === 'trueOdds' ? (
            <div className={styles.box}>
              <span className={styles.label}>True Odds:</span>
              <input
                className={styles.input}
                type="number"
                id="trueOdds"
                value={fields.trueOdds}
                onChange={handleInputChange}
                placeholder="Enter true odds"
              />
            </div>
          ) : (
            <div className={styles.box}>
              <span className={styles.label}>True Probability:</span>
              <input
                className={styles.input}
                type="text"
                id="trueProbability"
                value={fields.trueProbability}
                onChange={handleInputChange}
                onBlur={(e) => handleBlurPercentage(e, setFields)}
                onFocus={(e) => handleFocus(e, setFields)}
                placeholder="Enter 0-100"
              />
            </div>
          )}
          <div className={styles.box}>
            <span className={styles.label}>Fractional Kelly:</span>
            <input
              className={styles.input}
              type="text"
              id="fractionalKelly"
              value={fields.fractionalKelly}
              onChange={handleInputChange}
              onBlur={(e) => handleBlurPercentage(e, setFields)}
              onFocus={(e) => handleFocus(e, setFields)}
              placeholder="Enter 0-100"
            />
          </div>
          <button onClick={handleCalculate}>Calculate</button>
          <div className={`${styles.result} ${isResultVisible ? styles.resultVisible : ''}`} id="result" dangerouslySetInnerHTML={{ __html: result }}></div>
        </div>
        <div className={styles.howToUse}>
          <h2>How to Use This Calculator</h2>
          <p>
            The Kelly Criterion Calculator helps you determine the optimal bet size based on the provided odds, true odds, or true probability, and the fractional Kelly percentage.
          </p>
          <h3>Which mode of the calculator to use?</h3>
          <p>
            <strong>Using True Odds:</strong> This method calculates the optimal bet size using the provided odds and true odds.
          </p>
          <p>
            <strong>Using True Probability:</strong> This method calculates the optimal bet size using the provided odds and true probability.
          </p>
          <h3>Explanation of the individual inputs</h3>
          {calculationMethod === 'trueOdds' ? (
            <>
              <p>
                1. <strong>Provided Odds:</strong> Enter the odds provided by the bookmaker.
              </p>
              <p>
                2. <strong>True Odds:</strong> Enter the true odds of the event.
              </p>
            </>
          ) : (
            <>
              <p>
                1. <strong>Provided Odds:</strong> Enter the odds provided by the bookmaker.
              </p>
              <p>
                2. <strong>True Probability:</strong> Enter the true probability of the event as a percentage.
              </p>
            </>
          )}
          <p>
            3. <strong>Fractional Kelly:</strong> Enter the fractional Kelly percentage to use for the calculation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default KellyCriterionCalculator;